import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import type { NoteResponseDto } from "~/lib/api";
import { addNote, deleteNote, editNote, fetchNotes } from "~/lib/patient-notes";
import { formatAbsoluteTime } from "~/session/audit/format-time";

export interface NotesPanelProps {
  sessionId: string;
  patientId: string;
  /** The currently signed-in user's DB id. Edit/Delete are only shown on own notes. */
  currentUserId: string | null;
}

/**
 * Task 17: append-only clinical notes log for a patient. Any ward member may
 * add a note; authors may edit or delete their own. `fetchNotes` on mount;
 * add/edit/delete via the `~/lib/patient-notes` wrappers. `forbidden` results
 * toast rather than throw — the controls are only shown on own notes, so a
 * 403 means a stale page, not a UI bug.
 *
 * The add-note textarea is kept mounted across re-renders (not conditionally
 * rendered) to preserve focus (same lesson as the audit-log filter bar).
 */
export function NotesPanel({ sessionId, patientId, currentUserId }: NotesPanelProps): JSX.Element {
  const [notes, setNotes] = useState<NoteResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [addBody, setAddBody] = useState("");
  const [addSubmitting, setAddSubmitting] = useState(false);

  // Per-note edit state: noteId → body being edited (undefined = not editing).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");

  // Keep textarea ref stable to avoid focus loss on state updates.
  const addTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchNotes(sessionId, patientId).then((result) => {
      if (cancelled) return;
      setLoading(false);
      if (result.status === "ok") {
        setNotes(result.notes);
      }
      // not_found / error: silently set empty (session-level 404 handled by parent)
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, patientId]);

  const handleAddNote = async (): Promise<void> => {
    const body = addBody.trim();
    if (!body || addSubmitting) return;
    setAddSubmitting(true);
    try {
      const result = await addNote(sessionId, patientId, body);
      if (result.status === "ok") {
        setNotes((prev) => [...prev, result.note]);
        setAddBody("");
      } else if (result.status === "error") {
        toast.error(result.message);
      } else {
        toast.error("Could not add note. Please try again.");
      }
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleEditSave = async (noteId: string): Promise<void> => {
    const body = editBody.trim();
    if (!body) return;
    const result = await editNote(sessionId, patientId, noteId, body);
    if (result.status === "ok") {
      setNotes((prev) => prev.map((n) => (n.id === noteId ? result.note : n)));
      setEditingId(null);
    } else if (result.status === "forbidden") {
      toast.error("You can only edit your own notes.");
      setEditingId(null);
    } else if (result.status === "error") {
      toast.error(result.message);
    } else {
      toast.error("Could not edit note. Please try again.");
    }
  };

  const handleDelete = async (noteId: string): Promise<void> => {
    const result = await deleteNote(sessionId, patientId, noteId);
    if (result.status === "ok") {
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } else if (result.status === "forbidden") {
      toast.error("You can only delete your own notes.");
    } else if (result.status === "error") {
      toast.error(result.message);
    } else {
      toast.error("Could not delete note. Please try again.");
    }
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading notes…</p>
      ) : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No notes yet.</p>
      ) : (
        <ul className="space-y-3">
          {notes.map((note) => {
            const isOwn = currentUserId != null && note.authorId === currentUserId;
            const isEditing = editingId === note.id;

            return (
              <li key={note.id} className="rounded-lg border border-border p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    <span>{note.authorName}</span>
                    <span aria-hidden="true"> · </span>
                    <time dateTime={note.createdAt}>{formatAbsoluteTime(note.createdAt)}</time>
                  </span>
                  {isOwn ? (
                    <div className="flex gap-1">
                      {!isEditing ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto px-2 py-0.5 text-xs"
                          aria-label={`Edit note by ${note.authorName}`}
                          onClick={() => {
                            setEditingId(note.id);
                            setEditBody(note.body);
                          }}
                        >
                          Edit
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-auto px-2 py-0.5 text-xs text-destructive hover:text-destructive"
                        aria-label={`Delete note by ${note.authorName}`}
                        onClick={() => void handleDelete(note.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  ) : null}
                </div>
                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={3}
                      aria-label="Edit note body"
                      maxLength={2000}
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => void handleEditSave(note.id)}
                        disabled={!editBody.trim()}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap text-sm text-foreground">{note.body}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Add-note form — always mounted to preserve focus across re-renders. */}
      <div className="space-y-2">
        <Textarea
          ref={addTextareaRef}
          value={addBody}
          onChange={(e) => setAddBody(e.target.value)}
          placeholder="Add a note…"
          aria-label="Add a note"
          rows={3}
          maxLength={2000}
          disabled={addSubmitting}
        />
        <Button
          type="button"
          size="sm"
          disabled={addSubmitting || !addBody.trim()}
          onClick={() => void handleAddNote()}
        >
          {addSubmitting ? "Adding…" : "Add note"}
        </Button>
      </div>
    </div>
  );
}
